<script lang="ts">
	import { formInputColors } from '../../styles'
	import ErrorLabel from '../errorLabel/ErrorLabel.svelte'
	import { validateIp } from '../validations/validations'
	import LengthCounter from './LengthCounter.svelte'
	import LinesCounter from './LinesCounter.svelte'
	import { TextareaType } from './TextareaType'
	import { _ } from '@repo/i18n'
	import { Label, getContextErrors } from '@repo/ui'
	import { createEventDispatcher } from 'svelte'
	import { createBubbler } from 'svelte/legacy'

	const bubble = createBubbler()

	interface Props {
		value?: string
		values?: string[]
		lineValues?: string[]
		placeholder?: string
		label?: string
		description?: string
		name?: string
		required?: boolean
		type?: TextareaType
		code?: boolean
		seamless?: boolean
		maxlength?: any
		rich?: boolean
		hideLabel?: boolean
		readonly?: boolean
		noError?: boolean
		splitByLines?: boolean
		tooltip?: string
	}

	let {
		value = $bindable(''),
		values = null,
		lineValues = $bindable(null),
		placeholder = null,
		label = '',
		description = '',
		name = '',
		required = false,
		type = TextareaType.text,
		code = false,
		seamless = false,
		maxlength = null,
		rich = false,
		hideLabel = false,
		readonly = false,
		noError = false,
		splitByLines = false,
		tooltip = null,
	}: Props = $props()

	const errors = getContextErrors()
	const dispatch = createEventDispatcher()

	if (values) {
		value = values.join(',')
	}

	if (lineValues) {
		value = lineValues.join('\n')
	}

	let isFirstValidation = true

	function handleNativeInput() {
		if (splitByLines && lineValues !== null) {
			lineValues = value
				.split('\n')
				.map((v) => v.trim())
				.filter((v) => v.length > 0)

			if (!isFirstValidation) {
				validate(value)
			}
		}
	}

	function handleInput(event: CustomEvent) {
		value = event.detail
		dispatch('input', value)

		if (!isFirstValidation) {
			validate(value)
		}
	}

	function validate(value: string) {
		if (type === TextareaType.ips) {
			const ips = value
				.split('\n')
				.map((e) => e.trim())
				.filter((e) => e)

			const firstInvalidIp = ips.find((ip: string) => {
				return !validateIp(ip)
			})

			if (firstInvalidIp) {
				errors.upsert(name, {
					text: $_('form.invalidIpAddress', { values: { ip: firstInvalidIp } }),
				})
			} else {
				errors.upsert(name, null)
			}
		}

		if (splitByLines && lineValues) {
			const duplicates = lineValues.filter((item, index) => lineValues.indexOf(item) !== index)

			if (duplicates.length > 0) {
				const uniqueDuplicates = [...new Set(duplicates)]
				errors.upsert(name, {
					text: $_('form.duplicateItems', {
						values: {
							itemCount: uniqueDuplicates.length,
							duplicateList: uniqueDuplicates.join(', '),
						},
					}),
				})
			} else if (type !== TextareaType.ips) {
				errors.upsert(name, null)
			}
		}
	}

	function handleValidation(event: Event) {
		dispatch('blur')

		validate((event.target as HTMLInputElement).value)
		isFirstValidation = false
	}

	async function importTextEditor() {
		return await import('../textEditor/TextEditor.svelte')
	}
</script>

{#if !seamless && !hideLabel}
	<Label {name} {label} {required} {tooltip} />
{/if}

{#if rich}
	{#await importTextEditor() then TextEditor}
		<TextEditor.default
			{name}
			additionalClasses="rounded-md prose max-w-none dark:prose-invert block overflow-y-auto w-full min-h-48 max-h-[calc(90vh-var(--header-height))] w-full border border-gray-300 bg-transparent py-2 px-3 text-base text-gray-900 shadow-input
    {formInputColors}
    {code ? 'font-mono text-sm' : ''}
  "
			content={value}
			{readonly}
			{required}
			on:input={handleInput}
		/>
	{/await}
{:else}
	<textarea
		id={name}
		{name}
		class="
    shadow-input block w-full border border-gray-300 bg-transparent px-3 py-2 text-base text-gray-900
    {formInputColors}
    {code ? 'font-mono text-sm' : ''}
    {seamless ? 'rounded-tl-none rounded-tr-md rounded-b-md' : 'rounded-md'}
    {splitByLines ? '[field-sizing:content] resize-none' : 'h-48'}
  "
		class:border-red-700={errors && $errors?.[name]}
		{maxlength}
		onblur={handleValidation}
		onchange={bubble('change')}
		oninput={handleNativeInput}
		{placeholder}
		{required}
		bind:value
	></textarea>
{/if}
{#if maxlength}
	<LengthCounter {maxlength} {value} />
{/if}
{#if code && value}
	<LinesCounter {value} />
{/if}

{#if description}
	<div class="mt-1"></div>
	<div class="text-xs">
		{description}
	</div>
{/if}

{#if !noError}
	<ErrorLabel {name} />
{/if}
